import { BaseController } from '../../../base_controller';
import { countFlexRows } from 'utils/flex_rows';

const UNIQUE_ID = 'votes--dashboard--calendar-component';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_COLOR_VAR: Record<string, string> = {
  Setup: '--cal-st-setup',
  Payment: '--cal-st-payment',
  Ready: '--cal-st-ready',
  Running: '--cal-st-running',
  Ended: '--cal-st-ended',
};

const LEGEND_ORDER = [
  'Setup',
  'Payment',
  'Ready',
  'Running',
  'Ended',
];

// The three a reader does not plan a date around, and most of what crowded a busy month.
const DEFAULT_HIDDEN_STATUSES = ['Setup', 'Payment', 'Ended'];

const DEFAULT_STATUS_COLOR_VAR = '--cal-st-setup';

// Mirrors the SCSS `$screen-tablet - 1px` band.
const MENU_BREAKPOINT = '(max-width: 639px)';

// The fit ladder, roomiest first. mobile is the breakpoint's own arrangement, not a rung.
type HeadMode = 'wide' | 'tight' | 'compact' | 'mobile';

const TYPE_LABEL: Record<string, string> = {
  election: 'ElectionVote',
  motion: 'MeetingVote',
  announcement: 'Announcement',
};

interface CalendarEvent {
  id: number;
  name: string;
  type_of: string;
  status: string;
  start_date: string;
  end_date: string;
  turnout: string;
  vote_box_title: string | null;
  vote_box_url: string | null;
  organization_name: string | null;
}

interface DashboardGridRow {
  id: number | string;
  name: string;
  type_of: string;
  status_label: string | null;
  start_on: string | null;
  end_on: string | null;
  schedule_summary_html: string | null;
  vote_box_title: string | null;
  vote_box_url: string | null;
  organization_name: string | null;
}

interface DashboardGridDataResponse {
  rows: DashboardGridRow[];
  lastRow: number;
}

const SSRM_GRID_ID = 'application--toolkit--ag-grid-ssrm-component--ag-grid-ssrm-component';

// Structural: the calendar sits beside either grid, and only this much is common to both.
const CLIENT_GRID_ID = 'application--toolkit--ag-grid-component--ag-grid-component';

interface SsrmGridOutlet {
  switchToBasic(): void;
  switchToAdvanced(): void;
  resizeGrid(): void;
  setVoteBoxesHidden(hidden: boolean): void;
}

interface GridChromeOutlet {
  setGridChromeHidden(hidden: boolean): void;
}

const VOTE_BOXES_PANEL_SELECTOR = '.votes--dashboard--vote-boxes-panel-component';
const DASHBOARD_LAYOUT_SELECTOR = '.root-ag-dashboard-layout';
// Set by elections-root-ag-dashboard.scss, which owns the breakpoint.
const PANEL_SUPPRESSED_PROPERTY = '--vote-boxes-panel-suppressed';

const VOTE_BOXES_PANEL_TOGGLE_SELECTOR = '.js-root-ag-votebox-panel-toggle';

const GRID_TOGGLE_ACTIVE_CLASS = 'ag-grid-view-toggle__btn--active';

interface DashboardGridApi {
  getFilterModel(): Record<string, unknown>;
  addEventListener(eventType: string, listener: () => void): void;
  removeEventListener(eventType: string, listener: () => void): void;
}

const MAX_EVENTS_FETCH = 300;

// Session-only; the Rows control moves it, a reload comes back here.
const DEFAULT_MAX_SLOTS = 4;

interface WeekSegment {
  event: CalendarEvent;
  col: number;
  colEnd: number;
  lane: number;
  row: number;
  contL: boolean;
  contR: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const VIEW_PARAM = 'view';
const CALENDAR_VIEW_VALUE = 'calendar';

const stripTime = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const parseISODate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date: Date): string =>
  `${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getDate()}, ${date.getFullYear()}`;

const formatMonthDay = (date: Date): string =>
  `${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getDate()}`;

const formatDateRange = (start: Date, end: Date): string => {
  if (sameDay(start, end)) return formatDate(start);
  if (start.getFullYear() === end.getFullYear()) {
    return `${formatMonthDay(start)} – ${formatDate(end)}`;
  }
  return `${formatDate(start)} – ${formatDate(end)}`;
};

export default class extends BaseController {
  static targets = [
    'panel',
    'head',
    'headLeft',
    'monthTitle',
    'monthPicker',
    'pickerYear',
    'pickerGrid',
    'todayBtn',
    'slots',
    'menuWrap',
    'menuButton',
    'menu',
    'menuTodaySlot',
    'menuSlotsSlot',
    'menuLegendSlot',
    'weekdays',
    'grid',
    'legend',
    'slotSelect',
    'scheduledCount',
    'unscheduledNote',
    'detail',
    'detailName',
    'detailType',
    'detailStatus',
    'detailDates',
    'detailTurnout',
    'detailOpenLink',
    'dayAgenda',
    'dayAgendaTitle',
    'dayAgendaList',
  ];

  static values = {
    unscheduledCount: Number,
    planExpiry: String,
    dataUrl: String,
    showVoteBox: Boolean,
    linkVoteBox: Boolean,
    showOrganization: Boolean,
  };

  declare readonly panelTarget: HTMLElement;
  declare readonly headTarget: HTMLElement;
  declare readonly headLeftTarget: HTMLElement;
  declare readonly monthTitleTarget: HTMLButtonElement;
  declare readonly monthPickerTarget: HTMLElement;
  declare readonly pickerYearTarget: HTMLElement;
  declare readonly pickerGridTarget: HTMLElement;
  declare readonly todayBtnTarget: HTMLButtonElement;
  declare readonly slotsTarget: HTMLElement;
  declare readonly menuWrapTarget: HTMLElement;
  declare readonly menuButtonTarget: HTMLButtonElement;
  declare readonly menuTarget: HTMLElement;
  declare readonly hasMenuTarget: boolean;
  declare readonly menuTodaySlotTarget: HTMLElement;
  declare readonly menuSlotsSlotTarget: HTMLElement;
  declare readonly menuLegendSlotTarget: HTMLElement;
  declare readonly weekdaysTarget: HTMLElement;
  declare readonly gridTarget: HTMLElement;
  declare readonly legendTarget: HTMLElement;
  declare readonly slotSelectTarget: HTMLSelectElement;
  declare readonly scheduledCountTarget: HTMLElement;
  declare readonly unscheduledNoteTarget: HTMLElement;
  declare readonly detailTarget: HTMLElement;
  declare readonly detailNameTarget: HTMLElement;
  declare readonly detailTypeTarget: HTMLElement;
  declare readonly detailStatusTarget: HTMLElement;
  declare readonly detailDatesTarget: HTMLElement;
  declare readonly detailTurnoutTarget: HTMLElement;
  declare readonly detailOpenLinkTarget: HTMLAnchorElement;
  declare readonly dayAgendaTarget: HTMLElement;
  declare readonly dayAgendaTitleTarget: HTMLElement;
  declare readonly dayAgendaListTarget: HTMLElement;

  declare readonly unscheduledCountValue: number;
  declare readonly planExpiryValue: string;
  declare readonly dataUrlValue: string;
  declare readonly showVoteBoxValue: boolean;
  declare readonly linkVoteBoxValue: boolean;
  declare readonly showOrganizationValue: boolean;

  private loadFailed = false;
  private cursor: Date = new Date();
  private today: Date = new Date();
  private calendarToggleButtons: HTMLElement[] = [];
  private gridToggleButtons: HTMLElement[] = [];
  private mobileToggleButtons: HTMLElement[] = [];
  private panelToggleButtons: HTMLElement[] = [];
  private boundToggleCalendar: () => void = () => this.toggleCalendar();
  private boundSelectBasic: () => void = () => this.selectGridView('basic');
  private boundSelectAdvanced: () => void = () => this.selectGridView('advanced');
  private boundSelectGridFromMobile: (event: Event) => void = (event) =>
    this.selectGridViewFromMobile(event);
  private boundToggleVoteBoxesPanel: () => void = () => this.toggleVoteBoxesPanel();

  private gridApi: DashboardGridApi | null = null;
  private gridReadyHandler: (event: Event) => void = (event) =>
    this.attachGridApi(
      ((event as CustomEvent<{ gridApi: unknown }>).detail
        ?.gridApi as DashboardGridApi) ?? null
    );
  private boundFilterChanged: () => void = () => {
    this.monthCache.clear();
    if (this.activeView === 'calendar') void this.renderMonth();
  };

  private voteBoxesPanelOn = false;
  private panelSuppressed = false;
  private panelSuppressionHandler = (): void => this.syncPanelSuppression();

  private popstateHandler = (): void => {
    if (this.viewFromUrl() === 'calendar') {
      if (this.activeView !== 'calendar') this.showCalendar();
      return;
    }

    if (this.activeView === 'calendar') this.selectGridView(this.lastGridView);
  };

  private activeView: 'basic' | 'advanced' | 'calendar' = 'basic';
  private lastGridView: 'basic' | 'advanced' = 'basic';

  private loadedEvents: CalendarEvent[] = [];
  private monthCache = new Map<string, CalendarEvent[]>();
  private loadingMonth = false;
  private hiddenStatuses = new Set<string>(DEFAULT_HIDDEN_STATUSES);
  private maxSlots = DEFAULT_MAX_SLOTS;
  private legendButtons: HTMLButtonElement[] = [];

  private menuOpen = false;
  private menuQuery: MediaQueryList | null = null;
  // null, not a mode: the first sync has to apply whatever it measures.
  private headMode: HeadMode | null = null;
  private headObserver: ResizeObserver | null = null;
  private headWidth = -1;
  private fitFrame: number | null = null;
  private menuQueryHandler = (): void => this.scheduleHeadFit();
  private menuDismissHandler = (event: Event): void => {
    if (!this.menuWrapTarget.contains(event.target as Node)) this.closeMenu();
  };
  private menuKeydownHandler = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    this.closeMenu();
    this.menuButtonTarget.focus();
  };

  private monthPickerOpen = false;
  // Not the year being viewed: stepping the year previews without paging the calendar.
  private pickerYear = new Date().getFullYear();
  private monthPickerDismissHandler = (event: Event): void => {
    const wrap = this.monthPickerTarget.parentElement;
    if (!wrap?.contains(event.target as Node)) this.closeMonthPicker();
  };
  private monthPickerKeydownHandler = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    this.closeMonthPicker();
    this.monthTitleTarget.focus();
  };

  connect(): void {
    this.today = new Date();
    this.cursor = new Date(this.today.getFullYear(), this.today.getMonth(), 1);

    const restoreCalendar = this.viewFromUrl() === 'calendar';

    this.buildWeekdayHeader();
    this.buildLegend();
    this.slotSelectTarget.value = String(this.maxSlots);
    this.menuQuery = window.matchMedia(MENU_BREAKPOINT);
    this.menuQuery.addEventListener('change', this.menuQueryHandler);
    this.headObserver = new ResizeObserver(() => this.handleHeadResize());
    this.headObserver.observe(this.headTarget);
    this.syncHeadFit();
    this.wireExternalToggles();
    this.adoptInitialGridView();
    document.addEventListener('votes:dashboard:grid-ready', this.gridReadyHandler);
    document.dispatchEvent(
      new CustomEvent('votes:dashboard:request-grid-api')
    );
    this.syncSegmentState();
    this.panelSuppressed = this.readPanelSuppressed();
    this.applyPanelVisibility();
    window.addEventListener('resize', this.panelSuppressionHandler);
    window.addEventListener('popstate', this.popstateHandler);

    if (restoreCalendar) {
      this.showCalendar();
      return;
    }

    this.gridWrapper()?.classList.remove(
      'root-ag-dashboard-grid--calendar-active'
    );
    this.panelTarget.hidden = true;
    this.renderMonth();
  }

  disconnect(): void {
    this.calendarToggleButtons.forEach((btn) =>
      btn.removeEventListener('click', this.boundToggleCalendar)
    );
    const [basicBtn, advancedBtn] = this.gridToggleButtons;
    basicBtn?.removeEventListener('click', this.boundSelectBasic);
    advancedBtn?.removeEventListener('click', this.boundSelectAdvanced);
    this.mobileToggleButtons.forEach((btn) =>
      btn.removeEventListener('click', this.boundSelectGridFromMobile)
    );
    this.panelToggleButtons.forEach((btn) =>
      btn.removeEventListener('click', this.boundToggleVoteBoxesPanel)
    );
    document.removeEventListener(
      'votes:dashboard:grid-ready',
      this.gridReadyHandler
    );
    window.removeEventListener('resize', this.panelSuppressionHandler);
    window.removeEventListener('popstate', this.popstateHandler);
    this.menuQuery?.removeEventListener('change', this.menuQueryHandler);
    this.headObserver?.disconnect();
    if (this.fitFrame !== null) cancelAnimationFrame(this.fitFrame);
    this.closeMenu();
    this.closeMonthPicker();
    this.attachGridApi(null);
  }

  private attachGridApi(api: DashboardGridApi | null): void {
    if (this.gridApi === api) return;
    this.gridApi?.removeEventListener('filterChanged', this.boundFilterChanged);
    this.gridApi = api;
    this.gridApi?.addEventListener('filterChanged', this.boundFilterChanged);

    // A restored calendar opens before the grid exists, so its chrome survived the first sync.
    // Chrome only -- touching the panel here would purge and refetch every row for nothing.
    if (api && this.activeView === 'calendar') this.syncGridChrome();
  }

  private wireExternalToggles(): void {
    this.calendarToggleButtons = Array.from(
      document.querySelectorAll<HTMLElement>('.js-root-ag-calendar-toggle')
    );
    this.gridToggleButtons = Array.from(
      document.querySelectorAll<HTMLElement>('.ag-grid-view-toggle__btn')
    );
    this.mobileToggleButtons = Array.from(
      document.querySelectorAll<HTMLElement>('.ag-grid-view-toggle-mobile')
    );

    this.calendarToggleButtons.forEach((btn) =>
      btn.addEventListener('click', this.boundToggleCalendar)
    );

    const [basicBtn, advancedBtn] = this.gridToggleButtons;
    basicBtn?.addEventListener('click', this.boundSelectBasic);
    advancedBtn?.addEventListener('click', this.boundSelectAdvanced);

    this.mobileToggleButtons.forEach((btn) =>
      btn.addEventListener('click', this.boundSelectGridFromMobile)
    );

    this.panelToggleButtons = Array.from(
      document.querySelectorAll<HTMLElement>(VOTE_BOXES_PANEL_TOGGLE_SELECTOR)
    );
    this.panelToggleButtons.forEach((btn) =>
      btn.addEventListener('click', this.boundToggleVoteBoxesPanel)
    );
  }

  private adoptInitialGridView(): void {
    const [, advancedBtn] = this.gridToggleButtons;
    if (advancedBtn?.classList.contains(GRID_TOGGLE_ACTIVE_CLASS)) {
      this.activeView = 'advanced';
      this.lastGridView = 'advanced';
    }
  }

  toggleCalendar(): void {
    if (this.activeView === 'calendar') {
      this.selectGridView(this.lastGridView);
      return;
    }
    this.showCalendar();
  }

  showCalendar(): void {
    this.activeView = 'calendar';
    this.gridWrapper()?.classList.add(
      'root-ag-dashboard-grid--calendar-active'
    );
    this.panelTarget.hidden = false;
    void this.renderMonth();
    this.syncSegmentState();
    this.applyPanelVisibility();
    this.syncGridChrome();
    this.syncViewParam();
  }

  showGrid(): void {
    this.selectGridView(this.lastGridView);
  }

  private selectGridView(view: 'basic' | 'advanced'): void {
    this.activeView = view;
    this.lastGridView = view;
    this.gridWrapper()?.classList.remove(
      'root-ag-dashboard-grid--calendar-active'
    );
    this.panelTarget.hidden = true;
    this.closeDetail();
    this.syncSegmentState();
    this.applyPanelVisibility();
    this.applyMasterDetailMode(view);
    this.syncGridChrome();
    this.syncViewParam();
  }

  private applyMasterDetailMode(view: 'basic' | 'advanced'): void {
    const grid = this.ssrmGrid();
    if (!grid) return;

    if (view === 'basic') {
      grid.switchToBasic();
    } else {
      grid.switchToAdvanced();
    }
  }

  // These act on the grid alone, so the calendar hides them -- as the Basic view already does.
  private syncGridChrome(): void {
    this.gridChrome()?.setGridChromeHidden(this.activeView === 'calendar');
  }

  private gridChrome(): GridChromeOutlet | null {
    for (const identifier of [SSRM_GRID_ID, CLIENT_GRID_ID]) {
      const el = this.gridWrapper()?.querySelector<HTMLElement>(
        `[data-controller~="${identifier}"]`
      );
      if (!el) continue;

      const controller = this.application.getControllerForElementAndIdentifier(
        el,
        identifier
      ) as unknown as GridChromeOutlet | null;
      if (controller) return controller;
    }

    return null;
  }

  private ssrmGrid(): SsrmGridOutlet | null {
    const el = this.gridWrapper()?.querySelector<HTMLElement>(
      `[data-controller~="${SSRM_GRID_ID}"]`
    );
    if (!el) return null;

    return this.application.getControllerForElementAndIdentifier(
      el,
      SSRM_GRID_ID
    ) as unknown as SsrmGridOutlet | null;
  }

  private applyPanelVisibility(): void {
    if (this.activeView !== 'advanced' || this.panelSuppressed) {
      this.voteBoxesPanelOn = false;
    }

    const panelVisible = this.activeView === 'advanced' && this.voteBoxesPanelOn;
    const panel = this.voteBoxesPanelElement();
    if (panel) {
      panel.hidden = !panelVisible;
    }
    this.syncPanelToggleButtons();
    this.ssrmGrid()?.setVoteBoxesHidden(panelVisible);
    this.resizeGrid();
  }

  // Crossings only: applyPanelVisibility purges and refetches every row.
  private syncPanelSuppression(): void {
    const suppressed = this.readPanelSuppressed();
    if (suppressed === this.panelSuppressed) return;

    this.panelSuppressed = suppressed;
    this.applyPanelVisibility();
  }

  private readPanelSuppressed(): boolean {
    const layout = this.dashboardLayoutElement();
    if (!layout) return false;

    return (
      getComputedStyle(layout).getPropertyValue(PANEL_SUPPRESSED_PROPERTY).trim() === '1'
    );
  }

  private dashboardLayoutElement(): HTMLElement | null {
    return this.element.closest<HTMLElement>(DASHBOARD_LAYOUT_SELECTOR);
  }

  private voteBoxesPanelElement(): HTMLElement | null {
    return (
      this.dashboardLayoutElement()?.querySelector<HTMLElement>(
        VOTE_BOXES_PANEL_SELECTOR
      ) ?? null
    );
  }

  private toggleVoteBoxesPanel(): void {
    if (this.activeView !== 'advanced' || this.panelSuppressed) return;
    this.voteBoxesPanelOn = !this.voteBoxesPanelOn;
    this.applyPanelVisibility();
  }

  private syncPanelToggleButtons(): void {
    const isAdvanced = this.activeView === 'advanced';
    this.panelToggleButtons.forEach((btn) => {
      btn.hidden = !isAdvanced || this.panelSuppressed;
      btn.setAttribute('aria-pressed', this.voteBoxesPanelOn ? 'true' : 'false');
    });
  }

  private resizeGrid(): void {
    this.ssrmGrid()?.resizeGrid();
  }

  private selectGridViewFromMobile(event: Event): void {
    const btn = event.currentTarget as HTMLElement;
    const view: 'basic' | 'advanced' =
      btn.textContent?.trim() === 'Advanced' ? 'advanced' : 'basic';
    this.selectGridView(view);
  }

  private syncSegmentState(): void {
    this.calendarToggleButtons.forEach((btn) =>
      btn.setAttribute(
        'aria-pressed',
        this.activeView === 'calendar' ? 'true' : 'false'
      )
    );

    this.gridToggleButtons.forEach((btn, index) => {
      const view: 'basic' | 'advanced' = index === 0 ? 'basic' : 'advanced';
      btn.classList.toggle(GRID_TOGGLE_ACTIVE_CLASS, this.activeView === view);
    });
  }

  private gridWrapper(): HTMLElement | null {
    return this.element.closest<HTMLElement>('.root-ag-dashboard-grid');
  }

  prevMonth(): void {
    this.cursor = new Date(
      this.cursor.getFullYear(),
      this.cursor.getMonth() - 1,
      1
    );
    this.renderMonth();
  }

  nextMonth(): void {
    this.cursor = new Date(
      this.cursor.getFullYear(),
      this.cursor.getMonth() + 1,
      1
    );
    this.renderMonth();
  }

  goToToday(): void {
    this.cursor = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    this.closeMenu();
    this.closeMonthPicker();
    this.renderMonth();
  }

  toggleMonthPicker(): void {
    if (this.monthPickerOpen) this.closeMonthPicker();
    else this.openMonthPicker();
  }

  pickerPrevYear(): void {
    this.pickerYear -= 1;
    this.paintMonthPicker();
  }

  pickerNextYear(): void {
    this.pickerYear += 1;
    this.paintMonthPicker();
  }

  // Capture phase, so a click behind the picker closes it before that click lands.
  private openMonthPicker(): void {
    if (this.monthPickerOpen) return;

    this.closeMenu();
    this.monthPickerOpen = true;
    this.pickerYear = this.cursor.getFullYear();
    this.paintMonthPicker();
    this.monthPickerTarget.hidden = false;
    this.monthTitleTarget.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.monthPickerDismissHandler, true);
    document.addEventListener('keydown', this.monthPickerKeydownHandler);
  }

  private closeMonthPicker(): void {
    if (!this.monthPickerOpen) return;

    this.monthPickerOpen = false;
    this.monthPickerTarget.hidden = true;
    this.monthTitleTarget.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.monthPickerDismissHandler, true);
    document.removeEventListener('keydown', this.monthPickerKeydownHandler);
  }

  private paintMonthPicker(): void {
    this.pickerYearTarget.textContent = String(this.pickerYear);
    this.pickerGridTarget.innerHTML = '';

    MONTH_NAMES.forEach((name, month) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = this.b('month-picker-btn');
      btn.textContent = name.slice(0, 3);
      btn.title = `${name} ${this.pickerYear}`;

      if (this.pickerYear === this.today.getFullYear() && month === this.today.getMonth()) {
        btn.classList.add(this.b('month-picker-btn--today'));
      }

      if (
        this.pickerYear === this.cursor.getFullYear() &&
        month === this.cursor.getMonth()
      ) {
        btn.classList.add(this.b('month-picker-btn--on'));
        btn.setAttribute('aria-current', 'true');
      }

      btn.addEventListener('click', () => this.pickMonth(month));
      this.pickerGridTarget.appendChild(btn);
    });
  }

  private pickMonth(month: number): void {
    this.cursor = new Date(this.pickerYear, month, 1);
    this.closeMonthPicker();
    this.monthTitleTarget.focus();
    this.renderMonth();
  }

  toggleMenu(): void {
    if (this.menuOpen) this.closeMenu();
    else this.openMenu();
  }

  // Capture phase, so a click behind the panel closes it before that click lands.
  private openMenu(): void {
    if (this.menuOpen) return;

    this.menuOpen = true;
    this.menuTarget.hidden = false;
    this.menuButtonTarget.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.menuDismissHandler, true);
    document.addEventListener('keydown', this.menuKeydownHandler);
  }

  private closeMenu(): void {
    if (!this.menuOpen) return;

    this.menuOpen = false;
    if (this.hasMenuTarget) this.menuTarget.hidden = true;
    this.menuButtonTarget?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.menuDismissHandler, true);
    document.removeEventListener('keydown', this.menuKeydownHandler);
  }

  // Width only: our mode changes alter the height, which would feed the observer back into itself.
  private handleHeadResize(): void {
    const width = this.headTarget.clientWidth;
    if (width === this.headWidth) return;

    this.headWidth = width;
    this.scheduleHeadFit();
  }

  private scheduleHeadFit(): void {
    if (this.fitFrame !== null) cancelAnimationFrame(this.fitFrame);

    this.fitFrame = requestAnimationFrame(() => {
      this.fitFrame = null;
      this.syncHeadFit();
    });
  }

  // No breakpoint can stand in for this: the fit is the sum of every control's own width. All
  // rungs are applied and measured in one task, so the browser paints the outcome, never the tries.
  private syncHeadFit(): void {
    if (this.menuQuery?.matches) {
      this.applyHeadMode('mobile');
      return;
    }

    this.applyHeadMode('wide');
    if (this.headRowCount() <= 1) return;

    this.applyHeadMode('tight');
    if (this.headRowCount() <= 1) return;

    this.applyHeadMode('compact');
  }

  // Moving, not cloning: two Rows selects would hold their own state and drift apart on resize.
  private applyHeadMode(mode: HeadMode): void {
    if (mode === this.headMode) return;

    this.headMode = mode;
    this.closeMonthPicker();
    const menuHoldsControls = mode === 'compact' || mode === 'mobile';
    if (!menuHoldsControls) this.closeMenu();

    this.headTarget.classList.toggle(
      this.b('head--tight'),
      mode === 'tight' || mode === 'compact'
    );
    this.headTarget.classList.toggle(this.b('head--menu'), menuHoldsControls);

    if (mode === 'mobile') this.menuTodaySlotTarget.append(this.todayBtnTarget);
    else this.headLeftTarget.append(this.todayBtnTarget);

    if (menuHoldsControls) {
      this.menuSlotsSlotTarget.append(this.slotsTarget);
      this.menuLegendSlotTarget.append(this.legendTarget);
      return;
    }

    // Ahead of the menu wrapper, the head's last child -- restores their desktop order.
    this.headTarget.insertBefore(this.slotsTarget, this.menuWrapTarget);
    this.headTarget.insertBefore(this.legendTarget, this.menuWrapTarget);
  }

  // offsetParent, not the toolbar's size test: that would drop the zero-height head spacer.
  private headRowCount(): number {
    const items = Array.from(this.headTarget.children).filter(
      (child) => (child as HTMLElement).offsetParent !== null
    ) as HTMLElement[];

    return countFlexRows(items);
  }

  closeDetail(): void {
    this.detailTarget.removeAttribute('open');
  }

  backdropClick(event: MouseEvent): void {
    if (event.target === this.detailTarget) this.closeDetail();
  }

  private openDayAgenda(date: Date, events: CalendarEvent[]): void {
    this.dayAgendaTitleTarget.textContent = formatDate(date);

    this.dayAgendaListTarget.innerHTML = '';
    events.forEach((calendarEvent) => {
      this.dayAgendaListTarget.appendChild(this.buildAgendaRow(calendarEvent));
    });

    this.dayAgendaTarget.setAttribute('open', '');
  }

  closeDayAgenda(): void {
    this.dayAgendaTarget.removeAttribute('open');
  }

  dayAgendaBackdropClick(event: MouseEvent): void {
    if (event.target === this.dayAgendaTarget) this.closeDayAgenda();
  }

  private buildAgendaRow(calendarEvent: CalendarEvent): HTMLButtonElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = this.b('agenda-row');
    row.style.setProperty(
      '--cal-c',
      `var(${STATUS_COLOR_VAR[calendarEvent.status] ?? DEFAULT_STATUS_COLOR_VAR})`
    );

    const dot = document.createElement('span');
    dot.className = this.b('agenda-dot');

    const name = document.createElement('span');
    name.className = this.b('agenda-name');
    name.textContent = calendarEvent.name;

    const arrow = document.createElement('span');
    arrow.className = this.b('agenda-arrow');
    arrow.textContent = '›';

    row.append(dot, name, this.buildIdTag(calendarEvent));
    const agendaOrg = this.buildOrgTag(calendarEvent);
    if (agendaOrg) row.appendChild(agendaOrg);
    const boxIcon = this.buildBoxIcon(calendarEvent);
    if (boxIcon) row.appendChild(boxIcon);
    row.appendChild(arrow);

    row.addEventListener('click', () => {
      this.closeDayAgenda();
      this.openDetail(calendarEvent);
    });

    return row;
  }

  private planExpiryDate(): Date | null {
    if (!this.planExpiryValue) return null;
    return stripTime(parseISODate(this.planExpiryValue));
  }

  private isPlanExpiry(date: Date): boolean {
    const expiry = this.planExpiryDate();
    return expiry !== null && sameDay(date, expiry);
  }

  private async loadEvents(
    fromISO: string,
    toISO: string
  ): Promise<CalendarEvent[]> {
    this.loadFailed = false;
    if (!this.dataUrlValue) return [];

    try {
      const rows = await this.fetchGridDataRows(fromISO, toISO);

      return rows
        .filter((row) => !!row.start_on)
        .map((row) => this.mapRowToEvent(row));
    } catch (error) {
      this.loadFailed = true;
      // eslint-disable-next-line no-console
      console.error('[CalendarComponent] loadEvents fetch failed:', error);
      return [];
    }
  }

  private async fetchGridDataRows(
    fromISO: string,
    toISO: string
  ): Promise<DashboardGridRow[]> {
    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
        ?.content ?? '';

    const response = await fetch(this.dataUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        startRow: 0,
        endRow: MAX_EVENTS_FETCH,
        // A box row has no dates, so its children come up as loose rows instead -- otherwise a
        // boxed vote is absent from the month entirely.
        ...(this.showVoteBoxValue
          ? { excludeVoteBoxes: true, includeBoxedElections: true }
          : {}),
        sortModel: [{ colId: 'period', sort: 'asc' }],
        filterModel: this.calendarFilterModel(fromISO, toISO),
      }),
    });

    if (!response.ok) {
      throw new Error(`calendar grid_data fetch failed: ${response.status}`);
    }

    const data = (await response.json()) as DashboardGridDataResponse;
    return Array.isArray(data.rows) ? data.rows : [];
  }

  // Overlap, not "starts in this window": start_date alone drops a vote off every month but its
  // first.
  private calendarFilterModel(
    fromISO: string,
    toISO: string
  ): Record<string, unknown> {
    const filters: Record<string, unknown> = {
      ...(this.gridApi?.getFilterModel() ?? {}),
    };

    // A date filter carried over from the grid would intersect and blank out days of the month.
    delete filters.period;
    delete filters.start_date;
    delete filters.end_date;

    const status = filters.status;
    filters.status = {
      ...(status && typeof status === 'object' ? status : {}),
      filterType: 'statusTest',
      test: 'Not test',
    };

    return {
      ...filters,
      start_date: {
        filterType: 'date',
        type: 'lessThanOrEqual',
        dateFrom: toISO,
      },
      // A vote with no end is drawn on its start date, so it must survive the end-date half.
      end_date: {
        filterType: 'date',
        type: 'greaterThanOrEqual',
        dateFrom: fromISO,
        includeNull: true,
      },
    };
  }

  private mapRowToEvent(row: DashboardGridRow): CalendarEvent {
    return {
      id: Number(row.id),
      name: row.name,
      type_of: row.type_of,
      status: row.status_label ?? '',
      start_date: row.start_on as string,
      end_date: (row.end_on as string) ?? (row.start_on as string),
      turnout: this.extractTurnout(row.schedule_summary_html),
      vote_box_title: this.showVoteBoxValue ? row.vote_box_title ?? null : null,
      vote_box_url: this.linkVoteBoxValue ? row.vote_box_url ?? null : null,
      organization_name: this.showOrganizationValue ? row.organization_name ?? null : null,
    };
  }

  private extractTurnout(html: string | null): string {
    if (!html) return '—';

    const holder = document.createElement('div');
    holder.innerHTML = html;
    holder.querySelectorAll('div, p, li, br').forEach((el) => {
      el.parentNode?.insertBefore(document.createTextNode(' '), el);
    });
    const text = (holder.textContent ?? '').replace(/\s+/g, ' ').trim();
    return text || '—';
  }

  private async renderMonth(): Promise<void> {
    const key = this.monthKey(this.cursor);
    const cached = this.monthCache.get(key);

    if (cached) {
      this.loadedEvents = cached;
      this.loadingMonth = false;
      this.paintMonth();
      return;
    }

    // grid_data costs ~200ms; awaiting it before painting froze the calendar on every step.
    this.loadedEvents = [];
    this.loadingMonth = true;
    this.paintMonth();

    const weeks = this.weeksFor(this.cursor.getFullYear(), this.cursor.getMonth());
    const events = await this.loadEvents(
      this.toISO(weeks[0][0]),
      this.toISO(weeks[weeks.length - 1][6])
    );

    // Paging on while the request was in flight makes its answer the wrong month's.
    if (this.monthKey(this.cursor) !== key) return;

    if (!this.loadFailed) this.monthCache.set(key, events);
    this.loadedEvents = events;
    this.loadingMonth = false;
    this.paintMonth();
  }

  private monthKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}`;
  }

  // Draws what is already loaded, so a legend toggle never re-hits grid_data for the same month.
  private paintMonth(): void {
    const year = this.cursor.getFullYear();
    const month = this.cursor.getMonth();
    const weeks = this.weeksFor(year, month);
    const events = this.visibleEvents();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);

    // One inline element, so a month wider than the title box overflows evenly, not only right.
    this.monthTitleTarget.innerHTML =
      `<span class="${this.b('month-title-text')}">${MONTH_NAMES[month]} ` +
      `<span class="${this.b('year')}">${year}</span></span>`;

    // September is 30px wider than May -- enough to decide the head's fit on a tablet.
    this.scheduleHeadFit();

    const lanes = this.assignLanes(events, weeks[0][0], weeks[weeks.length - 1][6]);

    this.gridTarget.innerHTML = '';
    weeks.forEach((week) => {
      this.gridTarget.appendChild(this.buildWeekRow(week, month, events, lanes));
    });

    this.renderFooter(events, monthStart, monthEnd);
  }

  // On every mark: showing the id only where names collide reads as arbitrary to someone who
  // cannot see the collision.
  private buildIdTag(calendarEvent: CalendarEvent): HTMLElement {
    const tag = document.createElement('span');
    tag.className = this.b('evt-id');
    tag.textContent = String(calendarEvent.id);

    return tag;
  }

  // Null unless the server turned the seam on: a single-organization account gains nothing from
  // reading its own name on every bar.
  private buildOrgTag(calendarEvent: CalendarEvent): HTMLElement | null {
    if (!calendarEvent.organization_name) return null;

    const tag = document.createElement('span');
    tag.className = this.b('evt-org');
    tag.textContent = calendarEvent.organization_name;
    tag.title = calendarEvent.organization_name;

    return tag;
  }

  // Margin, not a space in the text: the pill is a flex container and collapses leading space.
  private buildMoreWord(): HTMLElement {
    const word = document.createElement('span');
    word.className = this.b('more-word');
    word.textContent = 'more';

    return word;
  }


  private visibleEvents(): CalendarEvent[] {
    if (this.hiddenStatuses.size === 0) return this.loadedEvents;

    return this.loadedEvents.filter(
      (calendarEvent) => !this.hiddenStatuses.has(calendarEvent.status)
    );
  }

  private toISO(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private weeksFor(year: number, month: number): Date[][] {
    const first = new Date(year, month, 1);
    const start = new Date(year, month, 1 - first.getDay());
    const weeks: Date[][] = [];
    let cursor = start;

    for (let w = 0; w < 6; w += 1) {
      const days: Date[] = [];
      for (let i = 0; i < 7; i += 1) {
        days.push(new Date(cursor));
        cursor = new Date(cursor.getTime() + DAY_MS);
      }
      weeks.push(days);
    }

    return weeks;
  }

  // Decided once for the whole grid, not per week: a bar can only be followed across a week
  // boundary if it holds its slot on the way. Shortest visible span takes the top slot.
  private assignLanes(
    events: CalendarEvent[],
    gridStart: Date,
    gridEnd: Date
  ): Map<number, number> {
    const firstDay = stripTime(gridStart).getTime();
    const lastDay = stripTime(gridEnd).getTime();

    // Days of THIS grid, not the vote's true length -- a March vote closing on the 3rd is three
    // days of clutter here, not five months of it.
    const visibleDays = (calendarEvent: CalendarEvent): number => {
      const start = Math.max(
        stripTime(parseISODate(calendarEvent.start_date)).getTime(),
        firstDay
      );
      const end = Math.min(
        stripTime(parseISODate(calendarEvent.end_date)).getTime(),
        lastDay
      );

      return end - start;
    };

    const ordered = [...events].sort(
      (a, b) =>
        visibleDays(a) - visibleDays(b) ||
        parseISODate(a.start_date).getTime() -
          parseISODate(b.start_date).getTime() ||
        parseISODate(a.end_date).getTime() -
          parseISODate(b.end_date).getTime() ||
        a.name.localeCompare(b.name)
    );

    // When each lane last fell free, which is what sinks long votes: they can only take a lane no
    // short vote needs for their whole span.
    const freeFrom: number[] = [];
    const assigned = new Map<number, number>();

    ordered.forEach((calendarEvent) => {
      const start = stripTime(parseISODate(calendarEvent.start_date)).getTime();
      const end = stripTime(parseISODate(calendarEvent.end_date)).getTime();
      let lane = freeFrom.findIndex((busyUntil) => start > busyUntil);
      if (lane === -1) {
        lane = freeFrom.length;
        freeFrom.push(end);
      } else {
        freeFrom[lane] = end;
      }
      assigned.set(calendarEvent.id, lane);
    });

    return assigned;
  }

  private layoutWeek(
    weekDays: Date[],
    events: CalendarEvent[],
    lanes: Map<number, number>
  ): WeekSegment[] {
    const weekStart = stripTime(weekDays[0]);
    const weekEnd = stripTime(weekDays[6]);
    const segments: WeekSegment[] = [];

    events.forEach((calendarEvent) => {
      const start = stripTime(parseISODate(calendarEvent.start_date));
      const end = stripTime(parseISODate(calendarEvent.end_date));
      if (end < weekStart || start > weekEnd) return;

      // A bar per week touched, crossings included: splitting off a day-anchored end chip left
      // the two ends in unrelated rows with nothing joining them.
      const segStart = start < weekStart ? weekStart : start;
      const segEnd = end > weekEnd ? weekEnd : end;

      segments.push({
        event: calendarEvent,
        col: Math.round((segStart.getTime() - weekStart.getTime()) / DAY_MS) + 1,
        colEnd: Math.round((segEnd.getTime() - weekStart.getTime()) / DAY_MS) + 2,
        lane: lanes.get(calendarEvent.id) ?? 0,
        row: 0,
        contL: start < weekStart,
        contR: end > weekEnd,
      });
    });

    return segments.sort((a, b) => a.lane - b.lane || a.col - b.col);
  }

  private buildWeekRow(
    week: Date[],
    month: number,
    events: CalendarEvent[],
    lanes: Map<number, number>
  ): HTMLElement {
    const segments = this.layoutWeek(week, events, lanes);
    const shown: WeekSegment[] = [];
    const hidden: WeekSegment[] = [];

    // Sparse grid-wide lanes collapse to consecutive slots, so unused lanes cost no height. Per
    // lane, not per segment: two votes sharing a lane share a slot.
    const rowForLane = new Map<number, number>();
    segments.forEach((segment) => {
      let row = rowForLane.get(segment.lane);

      if (row === undefined) {
        if (rowForLane.size >= this.maxSlots) {
          hidden.push(segment);
          return;
        }
        row = rowForLane.size;
        rowForLane.set(segment.lane, row);
      }

      segment.row = row;
      shown.push(segment);
    });

    const visibleLaneCount = rowForLane.size;
    const rowCount = visibleLaneCount + (hidden.length ? 1 : 0);

    const weekEl = document.createElement('div');
    weekEl.className = this.b('week');
    weekEl.style.gridTemplateRows = `auto repeat(${Math.max(rowCount, 1)}, 2.6rem)`;

    week.forEach((date) => {
      const cell = document.createElement('div');
      const inMonth = date.getMonth() === month;
      const classes = [this.b('day')];
      if (!inMonth) classes.push(this.b('day--dim'));
      if (sameDay(date, this.today)) classes.push(this.b('day--today'));
      cell.className = classes.join(' ');
      cell.innerHTML = `<div class="${this.b('daynum')}">${date.getDate()}</div>`;
      if (inMonth && this.isPlanExpiry(date)) {
        const flag = document.createElement('span');
        flag.className = this.b('plan-flag');
        flag.textContent = 'Plan ends';
        flag.title = `Plan ends ${formatDate(date)}`;
        cell.appendChild(flag);
      }
      weekEl.appendChild(cell);
    });

    shown.forEach((segment) => {
      weekEl.appendChild(this.buildEventBar(segment));
    });

    if (hidden.length) {
      const overflowByCol = new Map<number, number>();
      const endingsByCol = new Map<number, number>();

      hidden.forEach((segment) => {
        // A vote folded away on its closing day is the one omission worth colouring the pill for.
        const endsHere = !segment.contR;
        for (let col = segment.col; col < segment.colEnd; col += 1) {
          overflowByCol.set(col, (overflowByCol.get(col) ?? 0) + 1);
          if (endsHere && col === segment.colEnd - 1) {
            endingsByCol.set(col, (endingsByCol.get(col) ?? 0) + 1);
          }
        }
      });

      overflowByCol.forEach((count, col) => {
        weekEl.appendChild(
          this.buildMorePill(
            col,
            visibleLaneCount + 2,
            count,
            endingsByCol.get(col) ?? 0,
            week[col - 1],
            events
          )
        );
      });
    }

    return weekEl;
  }

  // What this column actually hid: the slot cap assumes every slot was spent on that day.
  private buildMorePill(
    col: number,
    row: number,
    count: number,
    endingCount: number,
    date: Date,
    events: CalendarEvent[]
  ): HTMLButtonElement {
    const dayEvents = this.eventsOnDay(date, events);

    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = this.b('more');
    pill.style.gridColumn = `${col} / ${col + 1}`;
    pill.style.gridRow = `${row}`;
    // Its own element so a narrow column can drop the word and keep the count.
    pill.replaceChildren(`+${count}`, this.buildMoreWord());
    pill.setAttribute('aria-label', `+${count} more`);
    if (endingCount > 0) pill.classList.add(this.b('more--ending'));
    pill.title =
      endingCount > 0
        ? `${dayEvents.length} elections on ${formatDate(date)} — ${endingCount} ending`
        : `${dayEvents.length} elections on ${formatDate(date)}`;
    pill.addEventListener('click', () => this.openDayAgenda(date, dayEvents));

    return pill;
  }

  // Week-crossing votes included: the grid draws them again, so excluding them was only correct
  // while a pass-through had no bar at all.
  private eventsOnDay(date: Date, events: CalendarEvent[]): CalendarEvent[] {
    const day = stripTime(date);

    return events
      .filter((calendarEvent) => {
        const start = stripTime(parseISODate(calendarEvent.start_date));
        const end = stripTime(parseISODate(calendarEvent.end_date));

        return start <= day && day <= end;
      })
      .sort(
        (a, b) =>
          parseISODate(a.start_date).getTime() -
            parseISODate(b.start_date).getTime() ||
          LEGEND_ORDER.indexOf(a.status) - LEGEND_ORDER.indexOf(b.status) ||
          a.name.localeCompare(b.name)
      );
  }

  private buildEventBar(segment: WeekSegment): HTMLButtonElement {
    const { event: calendarEvent } = segment;
    const bar = document.createElement('button');
    bar.type = 'button';

    const classes = [this.b('evt')];
    if (segment.contL) classes.push(this.b('evt--cont-l'));
    if (segment.contR) classes.push(this.b('evt--cont-r'));
    bar.className = classes.join(' ');

    bar.style.setProperty(
      '--cal-c',
      `var(${STATUS_COLOR_VAR[calendarEvent.status] ?? DEFAULT_STATUS_COLOR_VAR})`
    );
    bar.style.gridColumn = `${segment.col} / ${segment.colEnd}`;
    bar.style.gridRow = `${segment.row + 2}`;
    const span = formatDateRange(
      parseISODate(calendarEvent.start_date),
      parseISODate(calendarEvent.end_date)
    );
    bar.title = [
      `${calendarEvent.name} (${calendarEvent.id})`,
      calendarEvent.organization_name,
      span,
      calendarEvent.vote_box_title ? `Vote Box: ${calendarEvent.vote_box_title}` : null,
    ]
      .filter((part) => !!part)
      .join(' — ');

    const tag = document.createElement('span');
    tag.className = this.b('evt-tag');

    const name = document.createElement('span');
    name.className = this.b('evt-name');
    name.textContent = calendarEvent.name;

    bar.append(tag, name);

    bar.appendChild(this.buildIdTag(calendarEvent));
    const orgTag = this.buildOrgTag(calendarEvent);
    if (orgTag) bar.appendChild(orgTag);

    // Icon only: a day cell is far too narrow for the box title, which the detail card spells out.
    const boxIcon = this.buildBoxIcon(calendarEvent);
    if (boxIcon) bar.appendChild(boxIcon);

    if (segment.contR) {
      const arrow = document.createElement('span');
      arrow.className = this.b('evt-arrow');
      arrow.textContent = '›';
      bar.appendChild(arrow);
    }

    bar.addEventListener('click', () => this.openDetail(calendarEvent));

    return bar;
  }

  private renderFooter(
    events: CalendarEvent[],
    monthStart: Date,
    monthEnd: Date
  ): void {
    const scheduledCount = events.filter((calendarEvent) => {
      const start = parseISODate(calendarEvent.start_date);
      const end = parseISODate(calendarEvent.end_date);
      return !(end < monthStart || start > monthEnd);
    }).length;

    if (this.loadFailed) {
      this.scheduledCountTarget.textContent = '';
      this.unscheduledNoteTarget.textContent =
        'Could not load your elections. Refresh to try again.';
      return;
    }

    const expiry = this.planExpiryDate();
    const planNote = expiry ? `Plan ends ${formatDate(expiry)} · ` : '';
    this.scheduledCountTarget.textContent = this.loadingMonth
      ? `${planNote}Loading…`
      : `${planNote}${scheduledCount} scheduled this month`;

    if (this.unscheduledCountValue <= 0) {
      this.unscheduledNoteTarget.textContent = '';
      return;
    }

    const plural = this.unscheduledCountValue === 1 ? '' : 's';
    this.unscheduledNoteTarget.innerHTML =
      `<b>${this.unscheduledCountValue}</b> unscheduled election${plural} ` +
      'have no dates — <a class="' +
      this.b('link') +
      '" href="#">see them in the grid</a>.';
  }

  private viewFromUrl(): 'calendar' | 'grid' {
    const view = new URLSearchParams(window.location.search).get(VIEW_PARAM);

    return view === CALENDAR_VIEW_VALUE ? 'calendar' : 'grid';
  }

  // Writes only when the URL disagrees, so a restore or a popstate stacks no duplicate entry.
  private syncViewParam(): void {
    const view = this.activeView === 'calendar' ? 'calendar' : 'grid';
    if (this.viewFromUrl() === view) return;

    const url = new URL(window.location.href);
    if (view === 'calendar') url.searchParams.set(VIEW_PARAM, CALENDAR_VIEW_VALUE);
    else url.searchParams.delete(VIEW_PARAM);

    window.history.pushState({}, '', url);
  }

  private buildWeekdayHeader(): void {
    this.weekdaysTarget.innerHTML = WEEKDAY_NAMES.map(
      (day) => `<div>${day}</div>`
    ).join('');
  }

  private buildLegend(): void {
    this.legendTarget.replaceChildren(
      ...LEGEND_ORDER.map((status) => this.buildLegendButton(status))
    );
    this.legendButtons = Array.from(
      this.legendTarget.querySelectorAll<HTMLButtonElement>(`.${this.b('legend-btn')}`)
    );
    this.syncLegend();
  }

  private buildLegendButton(status: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = this.b('legend-btn');
    button.dataset.status = status;

    // On the button, not the dot: both reuse it (ring when off, border when on), and a custom
    // property only inherits downward.
    button.style.setProperty('--cal-dot', `var(${STATUS_COLOR_VAR[status]})`);

    const dot = document.createElement('i');
    dot.className = this.b('dot');

    button.append(dot, document.createTextNode(status));
    button.addEventListener('click', () => this.toggleStatus(status));

    return button;
  }

  changeSlots(event: Event): void {
    const next = Number.parseInt((event.target as HTMLSelectElement).value, 10);
    if (!Number.isInteger(next) || next < 1) return;

    this.maxSlots = next;
    this.closeMenu();
    this.paintMonth();
  }

  private toggleStatus(status: string): void {
    if (this.hiddenStatuses.has(status)) {
      this.hiddenStatuses.delete(status);
    } else {
      this.hiddenStatuses.add(status);
    }

    this.syncLegend();
    this.paintMonth();
  }

  private syncLegend(): void {
    this.legendButtons.forEach((button) => {
      const hidden = this.hiddenStatuses.has(button.dataset.status ?? '');
      button.classList.toggle(this.b('legend-btn--off'), hidden);
      button.setAttribute('aria-pressed', hidden ? 'false' : 'true');
      button.title = hidden
        ? `Show ${button.dataset.status} votes`
        : `Hide ${button.dataset.status} votes`;
    });
  }

  private openDetail(calendarEvent: CalendarEvent): void {
    this.renderDetailName(calendarEvent);
    this.renderDetailType(calendarEvent);

    this.detailStatusTarget.textContent = calendarEvent.status;
    this.detailStatusTarget.style.background = `var(${STATUS_COLOR_VAR[calendarEvent.status] ?? DEFAULT_STATUS_COLOR_VAR})`;

    this.detailDatesTarget.textContent = formatDateRange(
      parseISODate(calendarEvent.start_date),
      parseISODate(calendarEvent.end_date)
    );
    this.detailTurnoutTarget.textContent = calendarEvent.turnout;

    this.detailOpenLinkTarget.href = `/elections/${calendarEvent.id}/edit`;

    this.detailTarget.setAttribute('open', '');
  }

  private renderDetailName(calendarEvent: CalendarEvent): void {
    const name = document.createTextNode(calendarEvent.name);
    const chip = this.buildBoxChip(calendarEvent);

    this.detailNameTarget.replaceChildren(...(chip ? [name, chip] : [name]));
  }

  private buildBoxIcon(calendarEvent: CalendarEvent): HTMLElement | null {
    if (!calendarEvent.vote_box_title) return null;

    const icon = document.createElement('i');
    icon.className = `fas fa-archive ${this.b('box-icon')}`;
    icon.setAttribute('aria-hidden', 'true');

    return icon;
  }

  private buildBoxChip(calendarEvent: CalendarEvent): HTMLElement | null {
    if (!calendarEvent.vote_box_title) return null;

    // The admin dashboard sends no url, so the chip degrades to a badge rather than link to a 403.
    const chip = document.createElement(calendarEvent.vote_box_url ? 'a' : 'span');
    chip.className = this.b('box-chip');
    if (calendarEvent.vote_box_url) {
      (chip as HTMLAnchorElement).href = calendarEvent.vote_box_url;
    }

    const icon = document.createElement('i');
    icon.className = 'fas fa-archive';
    icon.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = this.b('box-chip-title');
    label.textContent = calendarEvent.vote_box_title;

    chip.append(icon, label);
    chip.title = calendarEvent.vote_box_url
      ? `Open Vote Box: ${calendarEvent.vote_box_title}`
      : `Vote Box: ${calendarEvent.vote_box_title}`;

    return chip;
  }

  private renderDetailType(calendarEvent: CalendarEvent): void {
    const label = document.createElement('span');
    label.className = this.b('card-type-label');
    label.textContent = TYPE_LABEL[calendarEvent.type_of] ?? calendarEvent.type_of;

    const id = document.createElement('span');
    id.className = this.b('card-id');
    id.textContent = String(calendarEvent.id);

    const parts: Node[] = [label, document.createTextNode(' '), id];
    if (calendarEvent.organization_name) {
      const org = document.createElement('span');
      org.className = this.b('card-org');
      org.textContent = calendarEvent.organization_name;
      parts.push(document.createTextNode(' '), org);
    }

    this.detailTypeTarget.replaceChildren(...parts);
  }

  private b(name: string): string {
    return `${UNIQUE_ID}__${name}`;
  }
}
